import { useLocalStorage } from '@partful/shared/lib/hooks/useLocalStorage';

export enum AvailableFlag {
  v2Wizard = 'v2Wizard',
  nonAnimatedHotspots = 'nonAnimatedHotspots',
  kitsFeedback = 'kitsFeedback',
}

export interface UseFeatureFlagReturn {
  /**
   * Function to check if a specific feature flag is enabled.
   * @param flag - The feature flag to check.
   * @returns {boolean} `true` if the flag is enabled, otherwise `false`.
   */
  isFlagOn: (flag: AvailableFlag) => boolean;
}

/**
 * Custom hook to check the status of feature flags stored in local storage.
 *
 * The hook uses the `useLocalStorage` utility to retrieve an array of feature flags
 * and provides a utility function `isFlagOn` to determine if a specific feature flag
 * is enabled.
 *
 * @returns {UseFeatureFlagReturn} An object containing:
 *   - `isFlagOn`: A function that checks if a specific feature flag is enabled.
 *
 * @example
 * ```typescript
 * import { useFeatureFlag, AvailableFlags } from './useFeatureFlag';
 *
 * const Component = () => {
 *   const { isFlagOn } = useFeatureFlag();
 *
 *   if (isFlagOn(AvailableFlags.v2Wizard)) {
 *     return <NewWizardComponent />;
 *   }
 *   return <OldWizardComponent />;
 * };
 * ```
 *
 * @see {@link AvailableFlag} for all available feature flags.
 */
export const useFeatureFlag = (): UseFeatureFlagReturn => {
  const [flags] = useLocalStorage<AvailableFlag[]>('flags', []);

  const isFlagOn = (flag: AvailableFlag) => flags.includes(flag);

  return { isFlagOn };
};
